import {
	MOSAIC_SIZE,
	MOSAIC_TILES,
	type MosaicState,
	type NumberGuessState,
	type PlayerIndex,
	rankLabel,
	SNAP_CARDS,
	type SnapState,
	type WarState,
} from "@playpal/core";
import { useState } from "react";
import { Button } from "../components/ui";

interface BoardProps<S> {
	state: S;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
}

// --- War of Cards ---------------------------------------------------------

export function WarBoard({ state, seat, onMove }: BoardProps<WarState>) {
	const mine = state.flipped[seat];
	const theirs = state.flipped[seat === 0 ? 1 : 0];
	const revealing = state.phase === "reveal";

	return (
		<div className="board-frame board-xl text-center">
			<p className="mb-4 text-lg font-semibold text-indigo-900/70 dark:text-indigo-100/70">
				Round {state.round + 1} of 12 · {state.scores[0]} – {state.scores[1]} cards won
				{state.pot > 0 && ` · ${state.pot} on the table`}
			</p>

			<div className="mb-6 flex items-center justify-center gap-6">
				<PlayingCard rank={mine} label="You" />
				<span className="text-2xl font-black">vs</span>
				<PlayingCard rank={revealing ? theirs : null} label="Them" />
			</div>

			{revealing ? (
				<p className="mb-4 text-xl font-bold">{state.lastRound?.because}</p>
			) : (
				<Button size="lg" disabled={mine !== null} onClick={() => onMove({ flip: true })}>
					{mine === null ? "🃏 Flip!" : "Waiting for your friend…"}
				</Button>
			)}

			<p className="mt-4 text-sm opacity-70">{state.counts[seat]} cards left in your pile</p>
		</div>
	);
}

function PlayingCard({ rank, label }: { rank: number | null; label: string }) {
	return (
		<span className="block">
			{/* Turns over rather than swapping its own text. Both ways round: the
			    next round deals a face-down card again, and the same transition
			    plays backwards. */}
			<span className="block h-32 w-24 rounded-2xl text-5xl font-black shadow-xl [perspective:800px]">
				<span className="flip" data-face={rank === null ? "front" : "back"}>
					<span className="flip-face rounded-2xl bg-indigo-500 text-indigo-200">?</span>
					<span className="flip-face flip-face-back rounded-2xl bg-white text-indigo-950">
						{rank === null ? "" : rankLabel(rank)}
					</span>
				</span>
			</span>
			<span className="mt-1 block text-sm font-bold opacity-70">{label}</span>
		</span>
	);
}

// --- Speed Snap -----------------------------------------------------------

export function SnapBoard({ state, onMove }: Omit<BoardProps<SnapState>, "seat">) {
	const [top, previous] = state.shown;

	return (
		<div className="board-frame board-xl text-center">
			<p className="mb-4 text-lg font-semibold text-indigo-900/70 dark:text-indigo-100/70">
				{/* Counted from the flips, not the deck: the deck is redacted to an
				    empty array before it reaches a client, so its length here would
				    read "2 cards to go" for the whole game. */}
				{state.scores[0]} – {state.scores[1]} · {SNAP_CARDS - state.flips} cards to go
			</p>

			<div className="mb-6 flex items-center justify-center gap-4">
				<PlayingCard rank={previous ?? null} label="Before" />
				<PlayingCard rank={top ?? null} label="Now" />
			</div>

			<button
				type="button"
				onClick={() => onMove({ snap: true })}
				className={`h-28 w-full rounded-3xl text-4xl font-black text-white shadow-xl transition ${
					state.pairUp ? "animate-wiggle bg-emerald-500" : "bg-indigo-500"
				}`}
			>
				SNAP!
			</button>

			<p className="mt-4 h-6 text-lg font-semibold" aria-live="polite">
				{state.lastEvent?.because ??
					(state.pairUp ? "A pair! Snap it!" : "Watch for two the same…")}
			</p>
		</div>
	);
}

// --- Number Guess Duel ----------------------------------------------------

export function NumberGuessBoard({ state, seat, onMove }: BoardProps<NumberGuessState>) {
	const myTurn = state.turn === seat && !state.result;
	const [value, setValue] = useState(50);

	return (
		<div className="board-frame board-lg text-center">
			<p className="mb-3 text-lg font-semibold">
				It's somewhere between {state.low} and {state.high}
			</p>

			<p className="mb-2 text-6xl font-black">{value}</p>
			<input
				type="range"
				min={state.low}
				max={state.high}
				value={Math.min(Math.max(value, state.low), state.high)}
				onChange={(event) => setValue(Number(event.target.value))}
				disabled={!myTurn}
				aria-label="Choose a number"
				className="mb-4 w-full accent-indigo-600"
			/>

			<Button
				size="lg"
				disabled={!myTurn}
				onClick={() => onMove({ guess: Math.min(Math.max(value, state.low), state.high) })}
			>
				Guess {Math.min(Math.max(value, state.low), state.high)}
			</Button>

			<ul className="mt-6 space-y-1 text-left">
				{[...state.guesses].reverse().map((entry) => (
					<li
						key={entry.value}
						className="flex items-center justify-between rounded-xl bg-white/60 px-4 py-2 font-semibold dark:bg-white/10"
					>
						<span>
							{entry.seat === seat ? "You" : "Them"} guessed {entry.value}
						</span>
						<span>
							{entry.hint === "got-it"
								? "🎉 that's it!"
								: entry.hint === "higher"
									? "⬆️ higher"
									: "⬇️ lower"}
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}

// --- Emoji Mosaic ---------------------------------------------------------

export function MosaicBoard({ state, seat, onMove }: BoardProps<MosaicState>) {
	const myTurn = state.turn === seat && !state.result;
	const [tile, setTile] = useState(0);

	return (
		<div className="board-frame board-lg">
			<p className="mb-2 text-center text-lg font-semibold">Copy this picture — {state.name}</p>

			<div className="mb-4 flex justify-center gap-6">
				<Grid
					cells={state.target.map((value) => MOSAIC_TILES[value])}
					label="The picture to copy"
					small
				/>
				<Grid
					cells={state.board.map((value) => (value === -1 ? "" : MOSAIC_TILES[value]))}
					label="Your copy"
					onCell={myTurn ? (square) => onMove({ square, tile }) : undefined}
					highlight={state.lastPlaced}
					correct={state.lastCorrect}
				/>
			</div>

			<div className="flex justify-center gap-2">
				{MOSAIC_TILES.map((emoji, index) => (
					<button
						key={emoji}
						type="button"
						aria-label={`Pick colour ${index + 1}`}
						aria-pressed={tile === index}
						onClick={() => setTile(index)}
						className={`rounded-2xl px-3 py-2 text-3xl transition ${
							tile === index
								? "scale-110 bg-indigo-600/90 shadow-lg"
								: "bg-white/60 dark:bg-white/10"
						}`}
					>
						{emoji}
					</button>
				))}
			</div>
			<p className="mt-3 text-center text-sm opacity-70">
				Pick a colour, then tap a square on your copy.
			</p>
		</div>
	);
}

function Grid({
	cells,
	label,
	onCell,
	small = false,
	highlight,
	correct,
}: {
	cells: string[];
	label: string;
	onCell?: (square: number) => void;
	small?: boolean;
	highlight?: number | null;
	correct?: boolean | null;
}) {
	return (
		<div className="text-center">
			<div
				className={`grid gap-0.5 rounded-xl bg-white/50 p-1 dark:bg-white/10 ${small ? "w-32" : "w-44"}`}
				style={{ gridTemplateColumns: `repeat(${MOSAIC_SIZE}, minmax(0, 1fr))` }}
			>
				{cells.map((emoji, square) =>
					onCell ? (
						<button
							key={square}
							type="button"
							disabled={emoji !== ""}
							aria-label={`Square ${square + 1}${emoji === "" ? ", empty" : ""}`}
							onClick={() => onCell(square)}
							className={`flex aspect-square items-center justify-center rounded text-lg transition enabled:hover:bg-white ${
								square === highlight
									? correct
										? "ring-2 ring-emerald-500"
										: "ring-2 ring-rose-400"
									: ""
							}`}
						>
							{emoji}
						</button>
					) : (
						<span
							key={square}
							className="flex aspect-square items-center justify-center text-sm"
							aria-hidden="true"
						>
							{emoji}
						</span>
					),
				)}
			</div>
			<p className="mt-1 text-xs font-semibold opacity-70">{label}</p>
		</div>
	);
}
